import React, { useEffect, useState } from "react";
import { useList, BaseKey, useApiUrl } from "@refinedev/core";
import {
  AutoComplete,
  Card,
  Col,
  Row,
  Typography,
  Affix,
  Progress,
  Button,
} from "antd";
import { Subject } from "interfaces";
import { AIQuestionGroup, AIQuestionItem } from "paper-splitter/interfaces";
import { axiosInstance } from "@refinedev/simple-rest";
import { ReloadOutlined } from "@ant-design/icons";
import { QuestionGroupModal } from "paper-splitter/components/QuestionGroupModal";
import { useTitle } from "contexts/TitleContext";

const { Title } = Typography;

type FilterOption = { label: string; value: BaseKey };

const getColour = (value: number) => {
  if (value >= 1) return "green";
  if (value > 0) return "orange";
  return "red";
};

export const QuestionAIGroups: React.FC = () => {
  const { setTitle } = useTitle();
  useEffect(() => {
    setTitle("Question Grouping");
  }, []);

  const [selectedSubject, setSelectedSubject] = useState<Subject | null>(null);
  const [questionGroups, setQuestionGroups] = useState<AIQuestionGroup[]>([]);
  const [selectedCard, setSelectedCard] = useState<AIQuestionGroup | null>(
    null
  );
  const [modalOpen, setModalOpen] = useState<boolean>(false);

  const [refreshing, setRefreshing] = useState(false);

  const apiUrl = useApiUrl();

  const { data: raw_subjects } = useList({
    resource: "group/subjects",
  });

  const subjects = (raw_subjects?.data as Subject[]) ?? [];
  const filterOptions: FilterOption[] =
    raw_subjects?.data.map((f) => ({ label: f.name, value: f.id! })) || [];

  const fetchQuestionGroups = async (subjectId: number | null = null) => {
    if (!subjectId && !selectedSubject) return;
    if (!subjectId && selectedSubject) subjectId = selectedSubject.id;

    setRefreshing(true);
    try {
      const response = await axiosInstance.get(
        `${apiUrl}/group/subjects/${subjectId}`
      );
      if (response.status === 200) {
        setQuestionGroups(response.data);
        if (selectedCard) {
          setSelectedCard((prev) => response.data.find((d: AIQuestionGroup) => d.topic.id === prev?.topic.id));
        }
      }
    } catch (error) {
      console.error(`Could not fetch resource due to: ${error}`);
    }
    setRefreshing(false);
  };

  const handleSubjectSelect = (value: any) => {
    if (!value) {
      setSelectedSubject(null);
      setQuestionGroups([]);
    }

    const foundSubject = subjects.find((s) => s.id === value.value) ?? null;
    if (foundSubject && foundSubject.id !== selectedSubject?.id)
      fetchQuestionGroups(foundSubject.id);
    setSelectedSubject(foundSubject);
  };

  const handleSelectCard = (card: AIQuestionGroup) => {
    setSelectedCard(card);
    handleModalOpen(true);
  };

  const handleModalOpen = (open: boolean) => {
    setModalOpen(open);
  };

  const handleCardQuestionsUpdate = async (questions: AIQuestionItem[]) => {
    if (selectedCard) {
      let newSelectedCard = { ...selectedCard, questions };
      setSelectedCard(newSelectedCard);
    }

    setQuestionGroups((prevQuestionGroups) =>
      prevQuestionGroups.map((group) => {
        if (group.topic.id === selectedCard?.topic.id) {
          group.questions = questions;
        }
        return group;
      })
    );

    try {
      await axiosInstance.put(`${apiUrl}/group/questions`, questions);
      await fetchQuestionGroups();
    } catch (error) {
      console.error(`Could not update questions due to: ${error}`);
    }
  };

  return (
    <div>
      <Affix offsetTop={0}>
        <div style={{ background: "#fff", padding: 16, zIndex: 10 }}>
          <Title level={1}>Question Groups</Title>
          <Row gutter={8} align="middle">
            <Col>
              <AutoComplete
                value={selectedSubject?.name}
                options={filterOptions}
                style={{ width: 300 }}
                placeholder="Select subject"
                onChange={(_, val) => handleSubjectSelect(val)}
                allowClear
              />
            </Col>
            <Col>
              {selectedSubject && (
                <Button
                  icon={<ReloadOutlined spin={refreshing} />}
                  onClick={() => fetchQuestionGroups(selectedSubject.id)}
                  loading={refreshing}
                />
              )}
            </Col>
          </Row>
        </div>
      </Affix>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        {questionGroups.map((group) => (
          <Col key={group.topic.id} span={6}>
            <Card
              title={group.topic.name}
              hoverable
              onClick={() => handleSelectCard(group)}
              style={{ textAlign: "center" }}
            >
              <Progress
                type="circle"
                percent={Math.round(
                  (group.total_points / group.target_points) * 100
                )}
                format={() => `${group.total_points} / ${group.target_points}`}
                strokeColor={getColour(
                  group.total_points / group.target_points
                )}
              />
            </Card>
          </Col>
        ))}
      </Row>

      {selectedCard && (
        <QuestionGroupModal
          card={selectedCard}
          modalOpen={modalOpen}
          refreshing={refreshing}
          onOpen={handleModalOpen}
          updateQuestions={handleCardQuestionsUpdate}
          fetchQuestionGroups={fetchQuestionGroups}
        />
      )}
    </div>
  );
};
